import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PageHeader,
  Panel,
  PanelHeader,
  Modal,
  EmptyState,
  ConfirmDialog,
} from "../components/admin";
import DataTable from "../components/admin/DataTable";
import useRowActionFeedback from "../components/admin/useRowActionFeedback";

/* ---------------------------------------------------------------------------
   Subscriptions — active subscriptions table + plan/feature management.
--------------------------------------------------------------------------- */

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const DURATIONS = ["Daily", "Weekly", "Monthly", "3 Months"];

function Subscriptions() {
  const [activeTab, setActiveTab] = useState("active");
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState({});
  const [editingFeature, setEditingFeature] = useState(null);
  const [newFeatures, setNewFeatures] = useState({});
  const [showNewPlanForm, setShowNewPlanForm] = useState(false);
  const [editingVotes, setEditingVotes] = useState(null);
  const [newPlan, setNewPlan] = useState({ planName: "", duration: "", price: "", maxVotes: 0 });
  const [editingPlan, setEditingPlan] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [cancelling, setCancelling] = useState(null);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const { busyId, flashId, flashKind, run } = useRowActionFeedback();

  useEffect(() => {
    fetchSubscriptions();
    fetchPlans();
    fetchAllFeatures();
  }, []);

  /* Fetch user names for the subscriptions table */
  useEffect(() => {
    const fetchUserDetails = async () => {
      const uniqueUserIds = [...new Set(subscriptions.map((sub) => sub.userId))];
      for (const userId of uniqueUserIds) {
        if (!userDetails[userId]) {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/users/${userId}`,
              { headers: authHeaders() }
            );
            setUserDetails((prev) => ({ ...prev, [userId]: response.data }));
          } catch (error) {
            console.error(`Error fetching user details for userId ${userId}:`, error);
          }
        }
      }
    };
    if (subscriptions.length > 0) fetchUserDetails();
  }, [subscriptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/subscriptions/all`, {
        headers: authHeaders(),
      });
      setSubscriptions(response.data);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/subscription-plans`, {
        headers: authHeaders(),
      });
      const plansWithVotes = await Promise.all(
        response.data.map(async (plan) => {
          try {
            const votesResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/subscription-plans/${plan.planId}/votes`,
              { headers: authHeaders() }
            );
            return { ...plan, maxVotes: votesResponse.data.maxVotes };
          } catch (error) {
            return { ...plan, maxVotes: 0 };
          }
        })
      );
      setPlans(plansWithVotes);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchAllFeatures = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/plan-features`, {
        headers: authHeaders(),
      });
      const featuresByPlan = response.data.reduce((acc, feature) => {
        if (!acc[feature.planId]) acc[feature.planId] = [];
        acc[feature.planId].push(feature);
        return acc;
      }, {});
      setFeatures(featuresByPlan);
    } catch (error) {
      console.error("Error fetching features:", error);
    }
  };

  const handleCancelSubscription = () =>
    run({
      id: cancelling.subscriptionId,
      action: async () => {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/subscriptions/${cancelling.subscriptionId}`,
          { active: false, endDate: new Date() },
          { headers: authHeaders() }
        );
        setCancelling(null);
      },
      success: "Subscription cancelled",
      error: "Could not cancel subscription",
      flash: "danger",
      refresh: fetchSubscriptions,
    }).catch(() => {});

  /* Plan feature CRUD */
  const addFeature = async (planId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/plan-features`,
        { feature: newFeatures[planId] || "", planId },
        { headers: authHeaders() }
      );
      setNewFeatures((prev) => ({ ...prev, [planId]: "" }));
      fetchAllFeatures();
    } catch (error) {
      console.error("Error adding feature:", error);
    }
  };

  const updateFeature = async (featureId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/plan-features/${featureId}`,
        editingFeature,
        { headers: authHeaders() }
      );
      setEditingFeature(null);
      fetchAllFeatures();
    } catch (error) {
      console.error("Error updating feature:", error);
    }
  };

  const deleteFeature = async (featureId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/plan-features/${featureId}`, {
        headers: authHeaders(),
      });
      fetchAllFeatures();
    } catch (error) {
      console.error("Error deleting feature:", error);
    }
  };

  /* Plan CRUD */
  const addPlan = async (planData) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/subscription-plans`, planData, {
        headers: authHeaders(),
      });
      fetchPlans();
    } catch (error) {
      console.error("Error adding plan:", error);
    }
  };

  const updatePlan = async (planId, planData) => {
    try {
      const currentPrice = planData.price?.startsWith("K")
        ? planData.price
        : `K${parseFloat(planData.price).toFixed(2)}`;
      await axios.put(
        `${process.env.REACT_APP_API_URL}/subscription-plans/${planId}`,
        {
          planName: planData.planName,
          duration: planData.duration,
          price: currentPrice,
        },
        { headers: authHeaders() }
      );
      fetchPlans();
    } catch (error) {
      console.error("Error updating plan:", error);
    }
  };

  const deletePlan = () =>
    run({
      id: deletingPlan.planId,
      action: async () => {
        await axios.delete(`${process.env.REACT_APP_API_URL}/subscription-plans/${deletingPlan.planId}`, {
          headers: authHeaders(),
        });
        setDeletingPlan(null);
      },
      success: "Plan deleted",
      error: "Could not delete plan",
      flash: "danger",
      refresh: fetchPlans,
    }).catch(() => {});

  const handleAddPlan = async (e) => {
    e.preventDefault();
    const formattedPlan = {
      ...newPlan,
      price: `K${parseFloat(newPlan.price).toFixed(2)}`,
      maxVotes: parseInt(newPlan.maxVotes),
    };
    await addPlan(formattedPlan);
    setNewPlan({ planName: "", duration: "", price: "", maxVotes: 0 });
    setShowNewPlanForm(false);
  };

  const updatePlanVotes = async (planId, maxVotes) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/subscription-plans/${planId}/votes`,
        { maxVotes },
        { headers: authHeaders() }
      );
      fetchPlans();
      setEditingVotes(null);
    } catch (error) {
      console.error("Error updating plan votes:", error);
    }
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

  const activeSubs = subscriptions.filter(
    (sub) => sub.startDate !== null && sub.endDate !== null
  );

  const subCols = [
    {
      key: "user",
      label: "Subscriber",
      render: (sub) => {
        const u = userDetails[sub.userId];
        return (
          <div>
            <p className="text-white font-medium leading-tight">{u?.name || "—"}</p>
            <p className="text-xs text-gray-500">{u?.email || sub.userId}</p>
          </div>
        );
      },
    },
    { key: "plan", label: "Plan", render: (sub) => <span className="text-gray-300">{sub.plan?.planName || "—"}</span> },
    { key: "price", label: "Price", render: (sub) => <span className="text-white">{sub.plan?.price || "—"}</span> },
    { key: "startDate", label: "Start", render: (sub) => <span className="text-gray-400">{fmtDate(sub.startDate)}</span> },
    { key: "endDate", label: "End", render: (sub) => <span className="text-gray-400">{fmtDate(sub.endDate)}</span> },
    {
      key: "active",
      label: "Status",
      render: (sub) => (
        <span className={sub.active ? "badge-success" : "badge-neutral"}>
          {sub.active ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Subscription Management"
        description="Manage active subscriptions, plans and plan features"
        actions={
          activeTab === "plans" ? (
            <button type="button" className="btn-primary" onClick={() => setShowNewPlanForm(true)}>
              Add new plan
            </button>
          ) : (
            <button type="button" className="btn-secondary" onClick={fetchSubscriptions}>
              Refresh
            </button>
          )
        }
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-line">
        {[
          { id: "active", label: `Active subscriptions (${activeSubs.length})` },
          { id: "plans", label: `Plans (${plans.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? "border-accent text-white"
                : "border-transparent text-gray-400 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "active" ? (
        <DataTable
          cols={subCols}
          rows={activeSubs}
          keyField="subscriptionId"
          busyId={busyId}
          busyField="subscriptionId"
          flashId={flashId}
          flashKind={flashKind}
          emptyTitle="No active subscriptions"
          emptyMessage="Subscriptions with a valid start and end date will appear here."
          rowActions={(sub) => (
            sub.active ? (
              <button type="button" className="btn-danger !py-1.5 !px-3" onClick={() => setCancelling(sub)}>
                Cancel
              </button>
            ) : null
          )}
        />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {plans.map((plan) => (
            <Panel key={plan.planId} padded={false}>
              <PanelHeader
                title={plan.planName}
                description={`${plan.duration} · ${plan.price}`}
                actions={
                  <>
                    <span className="badge-neutral mr-1">{plan.maxVotes?.toLocaleString()} votes</span>
                    <button type="button" className="btn-ghost !py-1.5 !px-3" onClick={() => setEditingVotes(plan)}>
                      Edit votes
                    </button>
                    <button type="button" className="btn-ghost !py-1.5 !px-3" onClick={() => setEditingPlan(plan)}>
                      Edit
                    </button>
                    <button type="button" className="btn-danger !py-1.5 !px-3" onClick={() => setDeletingPlan(plan)}>
                      Delete
                    </button>
                  </>
                }
              />
              <div className="p-5">
                <p className="eyebrow mb-2">Features</p>
                <div className="space-y-1.5">
                  {(features[plan.planId] || []).length === 0 ? (
                    <p className="text-sm text-gray-600">No features yet.</p>
                  ) : (
                    (features[plan.planId] || []).map((feature) => (
                      <div
                        key={feature.featureId}
                        className="flex items-center justify-between bg-surface-900 border border-line rounded-lg px-3 py-2"
                      >
                        <span className="text-sm text-gray-200">{feature.feature}</span>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setEditingFeature(feature)}
                            className="text-xs text-gray-400 hover:text-white"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteFeature(feature.featureId)}
                            className="text-xs text-accent-soft hover:text-accent"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a feature…"
                    value={newFeatures[plan.planId] || ""}
                    onChange={(e) =>
                      setNewFeatures((prev) => ({ ...prev, [plan.planId]: e.target.value }))
                    }
                    className="field !py-2"
                  />
                  <button type="button" className="btn-secondary shrink-0" onClick={() => addFeature(plan.planId)}>
                    Add
                  </button>
                </div>
              </div>
            </Panel>
          ))}
          {plans.length === 0 && (
            <Panel>
              <EmptyState
                title="No plans yet"
                message="Create your first subscription plan to get started."
                action={
                  <button type="button" className="btn-primary" onClick={() => setShowNewPlanForm(true)}>
                    Add new plan
                  </button>
                }
              />
            </Panel>
          )}
        </div>
      )}

      {/* New plan modal */}
      {showNewPlanForm && (
        <Modal title="Add new plan" onClose={() => setShowNewPlanForm(false)}>
          <form onSubmit={handleAddPlan} className="space-y-4">
            <div>
              <label className="field-label">Plan name</label>
              <input
                type="text"
                value={newPlan.planName}
                onChange={(e) => setNewPlan({ ...newPlan, planName: e.target.value })}
                className="field"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="field-label">Duration</label>
                <select
                  value={newPlan.duration}
                  onChange={(e) => setNewPlan({ ...newPlan, duration: e.target.value })}
                  className="field"
                  required
                >
                  <option value="">Select…</option>
                  {DURATIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Max votes</label>
                <input
                  type="number"
                  value={newPlan.maxVotes}
                  onChange={(e) => setNewPlan({ ...newPlan, maxVotes: e.target.value })}
                  className="field"
                  min="0"
                  required
                />
              </div>
            </div>
            <div>
              <label className="field-label">Price (ZMW)</label>
              <input
                type="number"
                step="0.01"
                value={newPlan.price}
                onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
                className="field"
                placeholder="0.00"
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setShowNewPlanForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">Create plan</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit plan modal */}
      {editingPlan && (
        <Modal title={`Edit plan · ${editingPlan.planName}`} onClose={() => setEditingPlan(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updatePlan(editingPlan.planId, editingPlan);
              setEditingPlan(null);
            }}
            className="space-y-4"
          >
            <div>
              <label className="field-label">Plan name</label>
              <input
                type="text"
                value={editingPlan.planName}
                onChange={(e) => setEditingPlan({ ...editingPlan, planName: e.target.value })}
                className="field"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="field-label">Duration</label>
                <select
                  value={editingPlan.duration}
                  onChange={(e) => setEditingPlan({ ...editingPlan, duration: e.target.value })}
                  className="field"
                  required
                >
                  <option value="">Select…</option>
                  {DURATIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Price (ZMW)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingPlan.price?.replace("K", "") || ""}
                  onChange={(e) => setEditingPlan({ ...editingPlan, price: e.target.value })}
                  className="field"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setEditingPlan(null)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">Save changes</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit votes modal */}
      {editingVotes && (
        <Modal title={`Edit votes · ${editingVotes.planName}`} onClose={() => setEditingVotes(null)}>
          <div className="space-y-4">
            <div>
              <label className="field-label">Max votes</label>
              <input
                type="number"
                value={editingVotes.maxVotes}
                onChange={(e) =>
                  setEditingVotes({ ...editingVotes, maxVotes: parseInt(e.target.value) })
                }
                className="field"
                min="0"
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setEditingVotes(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => updatePlanVotes(editingVotes.planId, editingVotes.maxVotes)}
              >
                Save changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel subscription confirm */}
      {cancelling && (
        <ConfirmDialog
          title="Cancel subscription"
          message={`Cancel ${userDetails[cancelling.userId]?.name || "this user"}'s ${cancelling.plan?.planName || ""} subscription? It will be marked inactive immediately.`}
          confirmLabel="Cancel subscription"
          onConfirm={handleCancelSubscription}
          onCancel={() => setCancelling(null)}
          busy={busyId === cancelling.subscriptionId}
        />
      )}

      {/* Delete plan confirm */}
      {deletingPlan && (
        <ConfirmDialog
          title="Delete plan"
          message={`Delete the "${deletingPlan.planName}" plan? Existing subscriptions keep their history. This cannot be undone.`}
          onConfirm={deletePlan}
          onCancel={() => setDeletingPlan(null)}
          busy={busyId === deletingPlan.planId}
        />
      )}

      {/* Edit feature modal */}
      {editingFeature && (
        <Modal title="Edit feature" onClose={() => setEditingFeature(null)}>
          <div className="space-y-4">
            <div>
              <label className="field-label">Feature</label>
              <input
                type="text"
                value={editingFeature.feature}
                onChange={(e) => setEditingFeature({ ...editingFeature, feature: e.target.value })}
                className="field"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setEditingFeature(null)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={() => updateFeature(editingFeature.featureId)}>
                Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Subscriptions;
